import { z } from 'zod';
import { uuid, isoTimestamp, syncColumns } from '../schemas/common.js';
import { RECEPT_SYNC_TABLES } from './enums.js';
import { cookedLogEntry } from './log.js';
/**
 * Row and protocol shapes for the Recepten module.
 *
 * Like agenda and todos, `household_id` is deliberately absent from the row — it is
 * derived from the session server-side, never a parameter a client sends — and the
 * module declares its own mutation union and pull response over its one table.
 *
 * The ingredient and step element schemas are nested zod arrays stored as jsonb
 * (the agenda participants/caregivers pattern).
 */
export const receptSyncTable = z.enum(RECEPT_SYNC_TABLES);
/* ---------------- ingredient + step elements ---------------- */
export const recipeIngredient = z.object({
    /**
     * The numeric amount, or null for an unmeasured item ("snufje zout", "scheut
     * olie"). This is what scales when the servings change; a null passes through
     * a rescale untouched (shared/src/recepten/scale.ts).
     */
    quantity: z.number().nullable(),
    /** Free-form Dutch unit: "g", "ml", "el", "teentjes", "bosje", … or null. */
    unit: z.string().max(40).nullable(),
    name: z.string().min(1).max(200),
    /** A parenthetical such as "fijngehakt", or null. */
    note: z.string().max(200).nullish(),
    /** An optional section heading ("Basics en extra's"), or null. */
    group: z.string().max(120).nullish(),
});
export const recipeStep = z.object({
    text: z.string().min(1).max(2000),
});
export const RECIPE_SOURCE_TYPES = ['photo', 'url', 'text', 'manual'];
export const recipeSourceType = z.enum(RECIPE_SOURCE_TYPES);
/* ---------------- row ---------------- */
export const receptRow = syncColumns.extend({
    id: uuid,
    title: z.string().min(1).max(200),
    /** The base number of servings the amounts are stated for. */
    servings: z.number().int().min(1).max(100),
    total_minutes: z.number().int().min(0).max(100_000).nullish(),
    ingredients: z.array(recipeIngredient).max(100),
    steps: z.array(recipeStep).max(100),
    tags: z.array(z.string().max(40)).max(20),
    source_type: recipeSourceType,
    source_url: z.string().max(2000).nullish(),
    /**
     * Where the recipe came from, as the household names it — a cookbook
     * ("Nistisima"), an app ("Crisp"), or the friend who came up with it. Free
     * text, distinct from `source_type` (which records the import mechanism); it is
     * chosen from the household's own existing sources or typed fresh, and is what
     * the overview filters on.
     */
    source_name: z.string().max(120).nullish(),
    image_path: z.string().max(500).nullish(),
    source_images: z.array(z.string().max(500)).max(12),
    notes: z.string().max(4000).nullish(),
    created_by: uuid.nullish(),
    /**
     * The cook history — one entry per time the recipe was cooked (migration 034,
     * shared/src/recepten/log.ts). Its length is how often the household cooks it;
     * an empty log (with an `added_at`) is what shows the "nieuw" badge. Nullish so
     * every recipe that predates the feature stays a valid row.
     */
    cooked_log: z.array(cookedLogEntry).nullish(),
    /**
     * When the household added the recipe — client-stamped at add time, so it's
     * correct and available immediately even offline, distinct from the infra
     * `created_at` (server-set at first sync). Nullish for pre-feature rows.
     */
    added_at: isoTimestamp.nullish(),
    /**
     * Whether the household has floated this recipe to the top of the overview
     * (migration 038, replacing the week planner). Nullish — a NULL reads as "not
     * pinned", so every recipe that predates the column stays a valid row.
     */
    pinned: z.boolean().nullish(),
    /**
     * The household's grade for the recipe, a Dutch cijfer 1–10 (migration 052) —
     * one per household, not per member. Nullish: null is "not graded yet".
     */
    grade: z.number().int().min(1).max(10).nullish(),
});
/* ---------------- mutations ---------------- */
const receptMutationId = z.object({ id: uuid });
const receptUpsert = receptMutationId.extend({
    op: z.literal('upsert'),
    table: z.literal('recepten'),
    row: receptRow,
});
/** A soft delete. Rows are never hard-deleted; the tombstone propagates. */
const receptDelete = receptMutationId.extend({
    op: z.literal('delete'),
    table: receptSyncTable,
    row: z.object({ id: uuid, updated_at: isoTimestamp }),
});
export const receptMutation = z.union([receptUpsert, receptDelete]);
export const receptPushRequest = z.object({
    mutations: z.array(receptMutation).max(500),
});
export const receptPushResponse = z.object({
    applied: z.array(uuid),
    rejected: z.array(z.object({ id: uuid, code: z.string(), message: z.string() })),
    server_time: isoTimestamp,
});
export const receptPullResponse = z.object({
    server_time: isoTimestamp,
    changes: z.object({
        recepten: z.array(z.record(z.unknown())),
    }),
    has_more: z.boolean(),
    cursor: z.string().optional(),
    full_resync: z.boolean().optional(),
});
/* ---------------- read: the recipe list ---------------- */
/**
 * `GET /api/v1/recepten`. The household's recipes, backing first paint, the
 * contract test and the acceptance walkthrough; the client normally computes the
 * list from its synced mirror. The element schema is the row itself.
 */
export const receptListResponse = z.object({
    recepten: z.array(receptRow),
});
/* ---------------- import (online, request-path) ---------------- */
/** A single uploaded image: base64 bytes plus its media type (no data-URI prefix). */
export const recipeImageInput = z.object({
    mediaType: z.string().max(60),
    data: z.string().max(24_000_000),
});
/**
 * Ask the server to parse a recipe from one of three sources. The endpoint is a
 * stateless parser — it returns an editable draft and writes nothing to
 * `recepten`; the client reviews the draft and saves it through its own sync
 * outbox (writes take one path).
 */
export const recipeImportRequest = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('photo'), images: z.array(recipeImageInput).min(1).max(6) }),
    z.object({ kind: z.literal('url'), url: z.string().url().max(2000) }),
    z.object({ kind: z.literal('text'), text: z.string().min(1).max(20_000) }),
]);
/**
 * A normalized crop rectangle within an uploaded image (0..1 of width/height),
 * marking where the finished-dish photo is. The vision model returns it for a
 * photo/screenshot import so the client can cut the food out of a tall screenshot
 * — otherwise the display picture is a centre-cropped "random part of the shot".
 */
export const recipeImageBox = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().min(0).max(1),
    h: z.number().min(0).max(1),
});
/** The editable draft the importer returns; source/image fields are set server-side. */
export const recipeDraft = z.object({
    title: z.string(),
    servings: z.number().int().min(1),
    total_minutes: z.number().int().nullable(),
    ingredients: z.array(recipeIngredient),
    steps: z.array(recipeStep),
    tags: z.array(z.string()),
    source_type: recipeSourceType,
    source_url: z.string().nullable(),
    /** A source the importer could infer (a website's name for a URL import); the reviewer edits it. */
    source_name: z.string().nullable(),
    image_path: z.string().nullable(),
    source_images: z.array(z.string()),
    /**
     * For a photo import: which uploaded image (0-based, in the order the client
     * sent them) shows the finished dish, and where in it, so the client can crop
     * the food out and store that as the display picture. Null for url/text imports
     * or when the model found no dish photo — the server's `image_path` stands.
     */
    image_index: z.number().int().min(0).nullable(),
    image_box: recipeImageBox.nullable(),
});
export const recipeImportResponse = z.object({ draft: recipeDraft });
/**
 * Store a client-produced image (a dish crop cut from an uploaded screenshot) and
 * get back the same-origin path to display it from. Content-addressed by bytes,
 * so re-storing the same crop is idempotent. Kept separate from `/import` because
 * cropping happens on the client, after the parse, where the original bytes live.
 */
export const recipeImageStoreRequest = recipeImageInput;
export const recipeImageStoreResponse = z.object({ path: z.string() });
/* ---------------- generate: recipes from the box (online, request-path) ---------------- */
/**
 * A recipe the generator proposes from a set of provided ingredients — the weekly
 * pakket box, plus anything the household typed. It holds the same content a saved
 * recipe does (title, servings, timing, ingredients, steps, tags) but no
 * source/image fields — those are set when the recipe is accepted — plus one extra
 * field, `uses_from_box`: the provided items this recipe actually uses. The screen
 * highlights them, and the add-to-list step pre-unticks them (you already have
 * them in the box). It is never stored; accepting a recipe maps it to a row.
 */
export const suggestedRecipe = z.object({
    title: z.string().min(1).max(200),
    servings: z.number().int().min(1).max(100),
    total_minutes: z.number().int().min(0).max(100_000).nullable(),
    ingredients: z.array(recipeIngredient).max(100),
    steps: z.array(recipeStep).max(100),
    tags: z.array(z.string().max(40)).max(20),
    uses_from_box: z.array(z.string().max(200)).max(100),
});
/**
 * `POST /api/v1/recepten/generations`. Start generating several recipes from a
 * list of ingredients (the pakket box, or whatever the household typed) plus
 * optional free-text directions that steer the whole batch.
 *
 * Generation is **asynchronous**: this returns immediately with a `pending`
 * generation, the model call runs detached on the server, and the client polls
 * `GET /recepten/generations/:id` until it is `ready`. That is because the recipe
 * model takes ~40s for a set — long enough that a phone abandons a held-open
 * request and the client mislabels the dropped connection as "offline". The batch
 * is stored server-side (staging), so it survives the app closing and both phones
 * see the same one. Like `/import`, the generator writes nothing to `recepten`;
 * the client reviews a chosen recipe and saves it through the sync outbox (writes
 * take one path).
 */
export const recipeSuggestRequest = z.object({
    ingredients: z.array(z.string().min(1).max(200)).max(60),
    /** Free-text batch directions: "vooral de courgette", "een keer met vis", "minder pittig". */
    wishes: z.string().max(1000).nullish(),
    count: z.number().int().min(1).max(6).default(3),
    servings: z.number().int().min(1).max(100).default(2),
    /** Where the box came from ("Pakket wk 36"), stored so a resumed batch keeps its label. */
    source_name: z.string().max(120).nullish(),
});
export const recipeGenerationStatus = z.enum(['pending', 'ready', 'failed']);
/**
 * One generation batch (a `recipe_generations` row). `recipes` is null until the
 * model finishes; `error` carries a Dutch reason on `failed`. The inputs ride
 * along so the client can restore the box/wishes when it resumes a batch on
 * reopen.
 */
export const recipeGeneration = z.object({
    id: z.string().uuid(),
    status: recipeGenerationStatus,
    ingredients: z.array(z.string()),
    wishes: z.string().nullable(),
    count: z.number().int(),
    servings: z.number().int(),
    source_name: z.string().nullable(),
    recipes: z.array(suggestedRecipe).nullable(),
    error: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
});
/** `POST /recepten/generations` and `GET /recepten/generations/:id`. */
export const recipeGenerationResponse = z.object({ generation: recipeGeneration });
/** `GET /recepten/generations` — recent batches, newest first, for resume-on-reopen. */
export const recipeGenerationsResponse = z.object({
    generations: z.array(recipeGeneration),
});
/**
 * `POST /api/v1/recepten/adjust`. Revise one generated recipe per a free-text
 * instruction ("maak 'm veganistisch", "meer knoflook", "zonder oven"). Stateless:
 * the recipe carries its own state, so each turn sends the current recipe and gets
 * a whole revised one back — a chat in the UI, single-shot on the wire, so the
 * narrow one-JSON-in/one-JSON-out model seam needs no conversation history.
 */
export const recipeAdjustRequest = z.object({
    recipe: suggestedRecipe,
    instruction: z.string().min(1).max(2000),
});
export const recipeAdjustResponse = z.object({ recipe: suggestedRecipe });
/**
 * The recepten config screen (`/recepten/instellingen`). One option in the model
 * picker: the id, a label, and an approximate cost per generated recipe in
 * *euro* cents (integer, rounded; a rough USD→EUR estimate for display only —
 * OpenRouter bills in USD, and 0 means "under a cent"). The rates and the
 * estimate math live server-side (`api/src/ai/pricing.ts`,
 * `api/src/recepten/models.ts`).
 */
export const recipeModelOption = z.object({
    id: z.string(),
    label: z.string(),
    est_cents_per_recipe: z.number().int().nonnegative(),
});
/**
 * `GET` and `PUT /api/v1/recepten/settings`. `recipe_model` is the household's
 * chosen model, or — until they pick one — the server default (`AI_RECIPE_MODEL`).
 * `models` is the selectable catalog with per-recipe estimates.
 */
export const recipeSettingsResponse = z.object({
    recipe_model: z.string(),
    models: z.array(recipeModelOption),
});
/** `PUT /api/v1/recepten/settings` body — the chosen model id (must be allowlisted). */
export const recipeSettingsRequest = z.object({
    recipe_model: z.string(),
});
//# sourceMappingURL=schemas.js.map